import React, { useCallback, useEffect, useState } from "react";
import useTimer from "hooks/useTimer";
import { v4 as uuidv4 } from "uuid";
import socket from "utils/socket";
import { useSelector } from "react-redux";

import GenericCard from "components/UI/GenericCard/GenericCard";
import {
  Divider,
  Row,
  Form,
  Col,
  InputNumber,
  Select,
  Button,
  Progress,
  Switch,
  Table,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import AntTable from "components/UI/AntTable";
import { apiSearchBacktestStrategies } from "api/apiPredictions";
import dayjs from "dayjs";
import { formatMomentDurationFull, returnUpDownClassname } from "utils/helpers";
import ModelDetails from "components/AiMagician/ModelDetails";
import getRangeFilterProps from "utils/tableRangeFilter";

const StrategiesExplorer = (props) => {
  const {
    shownPredictionResults,
    onLoadAiResult,
    selectedIds,
    onRemoveAiResult,
  } = props;
  const [isPending, setIsPending] = useState(false);
  const [results, setResults] = useState([]);
  const { startTimer, stopTimer, time } = useTimer();
  const [requestProgress, setRequestProgress] = useState(null);
  const [isError, setIsError] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [shownResults, setShownResults] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});

  const [form] = Form.useForm();

  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences.premiumFee
  );

  const handleSetDefaultFilters = () => {
    setFilteredInfo({
      isCorrupted: [false],
      avgProfit: [{ from: 6 }],
      averageDuration: [{ to: 3 }],
      strategySuccessRate: [{ from: 65 }],
      totalTransactions: [{ from: 100 }],
    });
  };

  const onLoadResult = (result) => {
    const backtestParams = form.getFieldsValue();
    onLoadAiResult(result, backtestParams);
  };

  // const handleSearchStrategiesSync = async (values) => {
  //   setIsPending(true);
  //   startTimer();
  //   try {
  //     const response = await apiSearchBacktestStrategies({
  //       queries: shownPredictionResults || [],
  //       params: values,
  //     });

  //     setResults(response);
  //   } catch (err) {}
  //   setIsPending(false);
  //   stopTimer();
  // };

  const handleSearchStrategiesAsync = async (values) => {
    setResults([]);
    setIsError(false);
    setIsPending(true);
    setRequestProgress(0);
    startTimer();
    try {
      const uuid = uuidv4();
      socket.on(uuid, (socketData) => {
        setResults((prevState) => [...socketData.results, ...prevState]);
        setRequestProgress(socketData.progress);
      });
      await apiSearchBacktestStrategies({
        uuid,
        queries: shownPredictionResults || [],
        params: values,
      });
    } catch (err) {
      setIsError(true);
    }
    setIsPending(false);
    stopTimer();
  };

  const updateEstimatedTime = () => {
    if (time === null || requestProgress === null) return;
    const estimated = (100 / requestProgress) * time - time;
    setEstimatedTime(estimated);
  };

  useEffect(() => {
    updateEstimatedTime();
  }, [requestProgress]);

  const sortByText = useCallback((a, b) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }, []);

  const sortByNumber = useCallback((a, b) => {
    // handle null / N/A values
    if (a === b) return 0;
    if (a === null || a === "N/A") return -1;
    if (b === null || b === "N/A") return 1;
    return a > b ? 1 : -1;
  }, []);

  function stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).substr(-2);
    }
    return color;
  }

  const columns = [
    {
      title: "Load",
      key: "uid",
      dataIndex: "",
      render: (text) =>
        selectedIds[text._id] ? (
          <Button onClick={() => onRemoveAiResult(text._id)}>Remove</Button>
        ) : (
          <Button onClick={() => onLoadResult(text)}>Load</Button>
        ),
    },
    {
      title: "Model",
      dataIndex: "model",
      render: (text, obj) => (
        <div
          className="flex justify-center items-center w-full"
          style={{
            borderLeft: stringToColor(text._id),
            borderLeftWidth: 2,
            borderLeftStyle: "solid",
          }}
        >
          <ModelDetails model={text} />
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByText(a.model.name, b.model.name),
        multiple: 1,
      },
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      sorter: {
        compare: (a, b) => sortByText(a.symbol, b.symbol),
        multiple: 2,
      },
    },
    {
      title: "Total Orders",
      dataIndex: "totalTransactions",
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.totalTransactions, b.totalTransactions),
      },
      ...getRangeFilterProps(["totalTransactions"]),
      filteredValue: filteredInfo.totalTransactions,
    },
    {
      title: "Success Rate",
      dataIndex: "successRate",
      render: (text) => <span>{text?.toLocaleString()}%</span>,
      sorter: {
        compare: (a, b) => sortByNumber(a.successRate, b.successRate),
      },
      ...getRangeFilterProps(["successRate"]),
    },
    {
      title: "Strategy Success Rate",
      dataIndex: "strategySuccessRate",
      render: (text) => <span>{text.toLocaleString()}%</span>,
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.strategySuccessRate, b.strategySuccessRate),
      },
      ...getRangeFilterProps(["strategySuccessRate"]),
      filteredValue: filteredInfo.strategySuccessRate,
    },
    // {
    //   title: "Total PNL",
    //   dataIndex: "pnl",
    //   render: (text) => (
    //     <span className={returnUpDownClassname(text)}>
    //       ${text.toLocaleString()}
    //     </span>
    //   ),
    //   sorter: {
    //     compare: (a, b) => sortByNumber(a.pnl, b.pnl),
    //   },
    //   ...getRangeFilterProps(["pnl"]),
    // },
    {
      title: "PNL %",
      dataIndex: "pnlPercent",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.pnlPercent, b.pnlPercent),
        multiple: 2,
      },
      ...getRangeFilterProps(["pnlPercent"]),
    },
    {
      title: "Average Duration",
      dataIndex: "averageDuration",
      sorter: {
        compare: (a, b) => sortByNumber(a.averageDuration, b.averageDuration),
      },
      ...getRangeFilterProps(["averageDuration"]),
      filteredValue: filteredInfo.averageDuration,
    },
    {
      title: "Avg Trade",
      dataIndex: "averageProfit",
      render: (text) => <span>{text.toLocaleString()}%</span>,
      sorter: {
        compare: (a, b) => sortByNumber(a.averageProfit, b.averageProfit),
      },
      ...getRangeFilterProps(["averageProfit"]),
    },
    {
      title: "Avg Profit",
      dataIndex: "avgProfit",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.avgProfit, b.avgProfit),
      },
      ...getRangeFilterProps(["avgProfit"]),
      filteredValue: filteredInfo.avgProfit,
    },
    {
      title: "Avg Loss",
      dataIndex: "avgLoss",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.avgLoss, b.avgLoss),
      },
      ...getRangeFilterProps(["avgLoss"]),
    },
    {
      title: "Highest Profit",
      dataIndex: "highestProfit",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.highestProfit, b.highestProfit),
      },
      ...getRangeFilterProps(["highestProfit"]),
    },
    {
      title: "Highest Loss",
      dataIndex: "highestLoss",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.highestLoss, b.highestLoss),
      },
      ...getRangeFilterProps(["highestLoss"]),
    },
    {
      title: "Buy Hold Return",
      dataIndex: "buyHoldReturn",
      render: (text) => <span>{text.toLocaleString()}%</span>,
      sorter: {
        compare: (a, b) => sortByNumber(a.buyHoldReturn, b.buyHoldReturn),
      },
      ...getRangeFilterProps(["buyHoldReturn"]),
    },
    {
      title: "Profit Factor",
      dataIndex: "profitFactor",
      sorter: {
        compare: (a, b) => sortByNumber(a.profitFactor, b.profitFactor),
      },
      ...getRangeFilterProps(["profitFactor"]),
    },
    {
      title: "Win-Loss Ratio",
      dataIndex: "winLossRatio",
      sorter: {
        compare: (a, b) => sortByNumber(a.winLossRatio, b.winLossRatio),
      },
      ...getRangeFilterProps(["winLossRatio"]),
    },
    {
      title: "Monthly Success Rate",
      dataIndex: "monthlySuccessRate",
      render: (text) => <span>{text?.toLocaleString()}%</span>,
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.monthlySuccessRate, b.monthlySuccessRate),
      },
      ...getRangeFilterProps(["monthlySuccessRate"]),
    },
    {
      title: "Avg. Annual Return",
      dataIndex: ["yearlyPnl", "average"],
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text?.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.yearlyPnl.average, b.yearlyPnl.average),
      },
      ...getRangeFilterProps(["yearlyPnl", "average"]),
    },
    {
      title: "Avg. Monthly Return",
      dataIndex: ["monthlyPnl", "average"],
      render: (text) => (
        <span className={returnUpDownClassname(text)}>
          %{text?.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) =>
          sortByNumber(a.monthlyPnl.average, b.monthlyPnl.average),
      },
      ...getRangeFilterProps(["monthlyPnl", "average"]),
    },
    {
      title: "From",
      dataIndex: "startDate",
      render: (text) => <>{dayjs(text).format("DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.startDate, b.startDate),
        multiple: 2,
      },
    },
    {
      title: "To",
      dataIndex: "endDate",
      render: (text) => <>{dayjs(text).format("DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.endDate, b.endDate),
      },
    },
    {
      title: "Corrupted",
      dataIndex: "isCorrupted",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          {text ? (
            <span style={{ fontSize: 22 }}>👮</span>
          ) : (
            <CloseOutlined style={{ fontSize: 22, color: "#ff0000" }} />
          )}
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.isCorrupted, b.isCorrupted),
      },
      filterMultiple: false,
      filters: [
        {
          text: "Yes",
          value: true,
        },
        {
          text: "No",
          value: false,
        },
      ],
      onFilter: (value, record) => record.isCorrupted === value,
      filteredValue: filteredInfo.isCorrupted,
    },
  ];

  const suffixSelector = (
    <Form.Item
      noStyle
      name="orderSizeType"
      required={false}
      rules={[
        {
          required: true,
          message: "",
        },
      ]}
    >
      <Select
        style={{
          width: 115,
        }}
      >
        <Select.Option value="USD">USD</Select.Option>
        <Select.Option value="percent">% of equity</Select.Option>
      </Select>
    </Form.Item>
  );

  const setOptionsParams = () => {
    form.setFieldsValue({
      userCommission: 0,
      takeProfitPercent: 3,
      stopLossPercent: -10,
      enterPositionBuyCount: 1,
      exitPositionNoBuyCount: 0,
      daysOpenExitCount: 7,
      initialBalance: 100000,
      orderSize: 10,
      orderSizeType: "percent",
      resetTrendAfterSell: false,
      premiumPerShare: 0.0,
      maxOpenOrders: 10,
    });
  };

  useEffect(() => {
    if (results) {
      setShownResults(results);
    }
  }, [results?.length]);

  useEffect(() => {
    document.title = "Strategies Explorer | Dindicator Dashboard";
  }, []);

  return (
    <>
      <h2>Industrial Backtest Strategies Explorer</h2>
      <br />
      <Form
        form={form}
        layout="vertical"
        name="overview"
        initialValues={{
          userCommission: userPremiumFee || 2.5,
          takeProfitPercent: 5,
          stopLossPercent: 0,
          enterPositionBuyCount: 1,
          exitPositionNoBuyCount: 0,
          daysOpenExitCount: 14,
          initialBalance: 1000,
          orderSize: 100,
          orderSizeType: "percent",
          resetTrendAfterSell: true,
          premiumPerShare: 0.0,
          maxOpenOrders: 1,
        }}
        onFinish={handleSearchStrategiesAsync}
      >
        <div className="cards-wrapper">
          <GenericCard title="Backtest Parameters">
            <Row gutter={8} align="bottom">
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Inital balance"
                  name="initialBalance"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={0} prefix="$" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Order size"
                  name="orderSize"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    addonAfter={suffixSelector}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Commission per share"
                  name="premiumPerShare"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    prefix="$"
                    step={0.01}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  label="Min Commission"
                  name="userCommission"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={0} prefix="$" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  label="Max open orders"
                  name="maxOpenOrders"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="Reset prediction trend after sell"
                  name="resetTrendAfterSell"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              hidden
              label="Buy predict count"
              name="enterPositionBuyCount"
              required={false}
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>

            <Divider orientation="left">Deal close rules</Divider>
            <Row gutter={8} align="bottom">
              <Col xs={24} sm={4}>
                <Form.Item
                  label="Take Profit"
                  name="takeProfitPercent"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={0} prefix="%" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item
                  label="Stop Loss"
                  name="stopLossPercent"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber max={0} prefix="%" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item
                  label="No buy count"
                  name="exitPositionNoBuyCount"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item
                  label="Bars Open Count"
                  name="daysOpenExitCount"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: "",
                    },
                  ]}
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <div className="flex gap-4 ps-4">
                <Form.Item>
                  <Button type="default" onClick={setOptionsParams}>
                    Set options params
                  </Button>
                </Form.Item>
                <Form.Item shouldUpdate>
                  {() => (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isPending}
                    >
                      Search
                    </Button>
                  )}
                </Form.Item>
              </div>
            </Row>
            {time !== null && <p>Elapsed time: {time} seconds</p>}
            {requestProgress !== null ? (
              <>
                <div
                  style={{
                    width: 170,
                  }}
                >
                  <Progress
                    percent={requestProgress}
                    status={`${isPending ? "active" : ""}${
                      isError ? "exception" : ""
                    }`}
                  />
                </div>
                {!isNaN(estimatedTime) && estimatedTime !== 0 ? (
                  <span>
                    Estimated remaining time:{" "}
                    {formatMomentDurationFull(estimatedTime * 1000)}
                  </span>
                ) : (
                  ""
                )}
              </>
            ) : (
              <></>
            )}
          </GenericCard>

          <GenericCard className="table-card " maxHeight={1000}>
            <div className="flex pb-4 gap-2">
              <Button type="default" onClick={handleSetDefaultFilters}>
                Set default filters
              </Button>
              <Button type="default" onClick={() => setFilteredInfo({})}>
                Reset filters
              </Button>
            </div>
            <AntTable
              rowKey="_id"
              dataSource={results}
              columns={columns}
              rowClassName={(obj) =>
                selectedIds[obj._id] ? "table-row-selected" : ""
              }
              scroll={{
                x: 2500,
                y: 400,
              }}
              onChange={(pagination, filters, sorter, extra) => {
                setShownResults(extra.currentDataSource);
                setFilteredInfo(filters);
              }}
              summary={(pageData) => {
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        Total results:{" "}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} colSpan={40}>
                        {pageData.length}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </GenericCard>
        </div>
      </Form>
    </>
  );
};

export default StrategiesExplorer;
